import { lazy } from 'react';
import { useParams } from 'react-router';
import { useBundleSurrenderSettings } from 'src/apis/surrender-service/use-bundle-surrender-settings';
import { useCamaTransaction } from 'src/apis/surrender-service/use-cama-transaction';
import { useEligibleSurrenderInquiryDeals } from 'src/apis/surrender-service/use-eligible-surrender-inquiry-deals';
import { useNetworkPointSurrenderSettings } from 'src/apis/surrender-service/use-network-point-surrender-settings';
import { useSurrenderRoot } from 'src/apis/surrender-service/use-surrender-root';
import { PageSpinner } from 'src/components/spinner-container';
import { Forbidden } from 'src/utils/errors';

const UnbundledPage = lazy(() => import('./page-unbundled'));
const BundledPage = lazy(() => import('./page-bundled'));
const CamaPage = lazy(() => import('./page-cama'));

export const CreateSurrenderRequest = () => {
  const surrenderRoot = useSurrenderRoot();
  const { networkPointId } = useParams<{ networkPointId?: string }>();
  const surrenderSettings = useNetworkPointSurrenderSettings({
    surrenderRoot,
    networkPointId,
  });
  const eligibleDeals = useEligibleSurrenderInquiryDeals({
    surrenderRoot,
    params: networkPointId ? { networkPointId } : undefined,
  });

  if (!surrenderRoot.response) return <PageSpinner />;
  if (
    !networkPointId ||
    !surrenderRoot.response.data._links.isSurrenderForAllEnabled
  )
    throw new Forbidden();
  if (!surrenderSettings?.response || !eligibleDeals?.response)
    return <PageSpinner />;

  return (
    <UnbundledPage
      networkPointId={networkPointId}
      surrenderRoot={surrenderRoot}
      surrenderSettings={surrenderSettings}
      eligibleDeals={eligibleDeals}
    />
  );
};

export const CreateBundleSurrenderRequest = () => {
  const surrenderRoot = useSurrenderRoot();
  const { bundleId } = useParams<{ bundleId?: string }>();
  const surrenderSettings = useBundleSurrenderSettings({
    surrenderRoot,
    bundleId,
  });
  const eligibleDeals = useEligibleSurrenderInquiryDeals({
    surrenderRoot,
    params: bundleId ? { bundleId } : undefined,
  });

  if (!surrenderRoot.response) return <PageSpinner />;
  if (!bundleId || !surrenderRoot.response.data._links.isSurrenderForAllEnabled)
    throw new Forbidden();
  if (!surrenderSettings?.response || !eligibleDeals?.response)
    return <PageSpinner />;

  return (
    <BundledPage
      bundleId={bundleId}
      surrenderRoot={surrenderRoot}
      surrenderSettings={surrenderSettings}
      eligibleDeals={eligibleDeals}
    />
  );
};

export const CreateCamaSurrenderRequest = () => {
  const surrenderRoot = useSurrenderRoot();
  const { transactionId } = useParams<{ transactionId?: string }>();
  const transaction = useCamaTransaction({ surrenderRoot, transactionId });

  if (!surrenderRoot.response) return <PageSpinner />;
  if (
    !transactionId ||
    !surrenderRoot.response.data._links.getAvailableRuntimes ||
    !surrenderRoot.response.data._links.createSurrenderRequest
  ) {
    throw new Forbidden();
  }
  if (!transaction?.response) return <PageSpinner />;

  return (
    <CamaPage
      transactionId={transactionId}
      surrenderRoot={surrenderRoot}
      transaction={transaction}
    />
  );
};
